import React, { useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Divider,
    Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Formik } from 'formik';
import * as yup from 'yup';
import get from "lodash/get";
import map from "lodash/map";
import find from "lodash/find";
import SaveIcon from '@mui/icons-material/Save';

import InputText from "../../../components/form/InputText";
import InputTextDetail from "../../../components/form/InputTextDetail";
import Autocomplete from "../../../components/form/AutoComplete";
import FormatMoney from "../../../components/form/FormatMoney";

const ServiceWatcher = ({ formik, serviceOptions }) => {
    useEffect(() => {
        const found = find(serviceOptions, ({ value }) => value === formik.values.service);
        formik.setFieldValue('comment', found ? found.label : '');
    }, [formik.values.service]);
    return null;
};

const itemValidator = (__) => yup.object({
    service:   yup.string().required(__(`form.service.error`)),
    quantity:  yup.number().typeError(__(`form.quantity.error`)).required(__(`form.quantity.error`)).min(1, __(`form.quantity.error`)),
    plantel:   yup.string().required(__(`form.plantel.error`)),
    unitPrice: yup.number().typeError(__(`form.unitPrice.error`)).required(__(`form.unitPrice.error`)).min(0.01, __(`form.unitPrice.error`)),
});

const ItemModal = ({ open, close, callback, __, item = null, isLoading = false }) => {
    const userData = JSON.parse(localStorage.getItem('userData'));

    const serviceOptions = map(userData?.codInvoices, ({ codInvoices, descriptions }) => ({
        value: codInvoices,
        label: descriptions,
    }));

    const plantelOptions = map(userData?.planteles, ({ plantelId, descripcion }) => ({
        value: plantelId,
        label: descripcion,
    }));

    const initialValues = {
        service:   get(item, "coD_FACTURA",  ""),
        quantity:  get(item, "cantidad",     ""),
        plantel:   get(item, "plantelId",    ""),
        comment:   get(item, "comentario",   ""),
        unitPrice: get(item, "cobro",        ""),
    };

    const calculateTotal = (quantity, unitPrice) => {
        const qty   = parseFloat(quantity)  || 0;
        const price = parseFloat(unitPrice) || 0;
        return (qty * price).toFixed(2);
    };

    const onSubmit = (values, { resetForm }) => {
        const serviceItem = find(serviceOptions, ({ value }) => value === get(values, "service"));
        callback({
            ...values,
            serviceLabel: get(serviceItem, "label", ""),
            priceTotal:   calculateTotal(get(values, "quantity"), get(values, "unitPrice")),
        });
        resetForm();
    };

    return (
        <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Typography component="h5" variant="h5">
                    {item ? __(`action.edit`) : __(`action.add`)} — {__(`labels.3`)}
                </Typography>
            </DialogTitle>
            <Divider />
            <Formik
                initialValues={initialValues}
                validationSchema={itemValidator(__)}
                onSubmit={onSubmit}
                enableReinitialize
            >
                {formik => (
                    <form onSubmit={get(formik, "handleSubmit")}>
                        <ServiceWatcher formik={formik} serviceOptions={serviceOptions} />
                        <DialogContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Autocomplete
                                        name="service"
                                        formik={formik}
                                        label={__(`form.service.label`)}
                                        placeholder={__(`form.service.placeholder`)}
                                        options={serviceOptions}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <InputText
                                        formik={formik}
                                        name="quantity"
                                        label={__(`form.quantity.label`)}
                                        placeholder={__(`form.quantity.placeholder`)}
                                        type="number"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        name="plantel"
                                        formik={formik}
                                        label={__(`form.plantel.label`)}
                                        placeholder={__(`form.plantel.placeholder`)}
                                        options={plantelOptions}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <InputTextDetail
                                        formik={formik}
                                        name="comment"
                                        label={__(`form.comment.label`)}
                                        disabled
                                        multiline
                                        rows={2}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <InputText
                                        formik={formik}
                                        name="unitPrice"
                                        label={__(`form.unitPrice.label`)}
                                        placeholder={__(`form.unitPrice.placeholder`)}
                                        InputProps={{ inputComponent: FormatMoney }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <InputTextDetail
                                        formik={formik}
                                        name="priceTotal"
                                        label={__(`form.priceTotal.label`)}
                                        disabled
                                        value={`$ ${calculateTotal(formik.values.quantity, formik.values.unitPrice)}`}
                                    />
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <Divider />
                        <DialogActions className='p-4'>
                            <Button variant="outlined" color="secondary" onClick={close}>
                                {__(`action.cancel`)}
                            </Button>
                            <LoadingButton
                                variant="contained"
                                color="secondary"
                                startIcon={<SaveIcon />}
                                type="submit"
                                loading={isLoading}
                            >
                                {__(`action.save`)}
                            </LoadingButton>
                        </DialogActions>
                    </form>
                )}
            </Formik>
        </Dialog>
    );
};

export default ItemModal;
